/**
 * GroupManagementModal Component
 * 
 * Modal for managing group agents and certificates from the groups list
 * Features tabbed interface for switching between agents and certificates
 * 
 * @component
 */

import { FC, useState } from 'react';
import { FiCpu, FiShield } from 'react-icons/fi';
import Modal from '@/components/Common/Modal';
import GroupAgentsSection from '@/components/GroupAgentsSection';
import GroupCertificatesSection from '@/components/GroupCertificatesSection';
import type { GroupManagementModalProps, TabType } from './GroupManagementModal.types';
import styles from './GroupManagementModal.module.css';

const GroupManagementModal: FC<GroupManagementModalProps> = ({
  open,
  onClose,
  groupId,
  groupName,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('agents');

  /**
   * Handle tab change
   */
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  /**
   * Handle update - refetch and notify parent
   */
  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={groupName}
      description="Gerencie agents e certificados vinculados a este grupo"
      size="xl"
    >
      <div className={styles.modalContainer}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'agents' ? styles.tabActive : ''}`}
            onClick={() => handleTabChange('agents')}
          >
            <FiCpu size={18} />
            <span>Agents</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'certificates' ? styles.tabActive : ''}`}
            onClick={() => handleTabChange('certificates')}
          >
            <FiShield size={18} />
            <span>Certificados</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.content}>
          {activeTab === 'agents' && (
            <GroupAgentsSection
              groupId={groupId}
              onUpdate={handleUpdate}
            />
          )}
          
          {activeTab === 'certificates' && (
            <GroupCertificatesSection
              groupId={groupId}
              onUpdate={handleUpdate}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default GroupManagementModal;
